import React, { useState, useEffect } from 'react';
import { TfiMedall } from 'react-icons/tfi';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
// import ClassModuleItem from '@/pages/LmsStudent/ClassModuleItem';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import moreMedal from '@/assets/images/more-medal.svg';
import { CourseModuleAboutTabAbout } from './course-module-about-tab-about';
import { CourseModuleAboutTabFiles } from './course-module-about-tab-files';
import { CourseModuleAboutTabActivities } from './course-module-about-tab-activities';
import { CourseModuleAboutTabBooklet } from './course-module-about-tab-booklet';
import { useSession } from '@/hooks/useSession';
import { Loader } from '@/components/custom/loader';
import { t } from 'i18next';
import usePermission from '@/hooks/usePermission';
import { IconVerified } from '@/components/custom/icon';
import { DashboardContent } from '@/layouts/dashboard';
import { Card, CardContent, CardProgressWrapper } from '@/components/ui/card';
import { FilterActions } from '@/components/custom/filter';
import { Button } from '@/components/ui/button';
import { CourseBanner, CourseBannerContainer, CourseBannerContent, CourseBannerCTA, CourseBannerHat, CourseBannerTitle } from '@/components/custom/course';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';

export function CourseModuleAbout(props) {
  const { uri } = props;

  const navigate = useNavigate();
  const { abilities } = usePlatformAbilities();
  const params = useParams();
  const client = useClient();
  const { session } = useSession();
  const authorityLevel = useAuthorityLevel();
  const [currentModule, setCurrentModule] = useState([]);
  const [loading, setLoading] = useState(false);

  const userId = session?.user?.id;

  const getCurrentModule = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`/learning_systems/${params.moduleId}?detailed=true`);
    if (error) {
      toast.error('Falha ao carregar módulo');
    } else {
      setCurrentModule(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    getCurrentModule();
  }, []);

  const downloadCertificate = async () => {
    window.open(`${process.env.REACT_APP_DEV_URL}//user_certificates/download/${currentModule?.['user-certificate-hash-id']}.pdf`, '_blank');
  };

  const isModuleEnabled = currentModule?.['learning-system-requirements']?.every(item => item.percent === 100);

  const breadCrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      {
        route: '/curso',
        name: 'Cursos',
        isActive: false
      },
      {
        route: authorityLevel !== 'student' ? `/curso/admin/${currentModule?.course?.id}` : `/curso/acompanhamento/${currentModule?.course?.id}`,
        name: currentModule?.course?.title,
        isActive: false
      },
      {
        route: uri,
        name: currentModule?.title,
        isActive: true
      }
    ]
  };

  const BASE_ROUTE = `/curso/${props.courseId}/modulo/${props.moduleId}`;

  if (loading) {
    return (
      <DashboardContent>
        <Loader />
      </DashboardContent>
    );
  }

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadCrumbs}
      breadcrumbsActions={
        authorityLevel !== 'student' && usePermission('Gerenciar conteúdo', 'Cursos') ? (
          <FilterActions className="pb-3">
            <Button
              variant="outline"
              size="sm"
              wide
              asChild
            >
              <Link to={`/curso/admin/gerenciar/${currentModule?.course?.id}/${currentModule.id}/disciplinas-e-aulas/undefined`}>{t('button.manageContent')}</Link>
            </Button>
          </FilterActions>
        ) : null
      }
    >
      <Card className="mb-6">
        <CardContent className="w-full flex-shrink-0">
          <CourseBanner
            backgroundUrl={currentModule?.image?.url}
            className="min-h-[224px]"
          >
            <CourseBannerContent>
              <CourseBannerContainer>
                <CourseBannerHat className="flex gap-1">
                  {currentModule?.['official-content'] && <IconVerified />}
                  {currentModule?.course?.title}
                  {authorityLevel === 'student' && !currentModule?.['learning-system-users']?.find(item => item?.['user-id'] === parseInt(userId)) && (
                    <Badge
                      variant="warning"
                      size="xs"
                    >
                      {t('lms.tagNew')}
                    </Badge>
                  )}
                  {currentModule?.progress === 100 && (
                    <Badge
                      variant="success"
                      size="xs"
                    >
                      Concluído
                    </Badge>
                  )}
                </CourseBannerHat>

                <CourseBannerTitle>{currentModule?.title}</CourseBannerTitle>

                {authorityLevel === 'student' && (
                  <CardProgressWrapper
                    variant="cover"
                    percentage={currentModule?.progress ? parseInt(currentModule?.progress) : 0}
                  />
                )}

                {authorityLevel === 'student' && isModuleEnabled ? (
                  <CourseBannerCTA>
                    <Button
                      variant="outline"
                      wide
                      asChild
                    >
                      <Link to={`/show-course/${currentModule?.id}/classroom`}>
                        {currentModule.progress === 100 ? 'Visualizar conteúdo' : currentModule?.['learning-system-users']?.find(item => item?.['user-id'] === parseInt(userId)) ? t('lms.buttonContinue') : t('lms.buttonStartModule')}
                      </Link>
                    </Button>

                    {currentModule?.['user-certificate-hash-id'] ? (
                      <Button
                        variant="outline-third"
                        wide
                        onClick={() => {
                          downloadCertificate();
                        }}
                      >
                        {t('button.certification')}
                      </Button>
                    ) : null}
                  </CourseBannerCTA>
                ) : null}
              </CourseBannerContainer>

              {currentModule?.progress === 100 && (
                <div className="medal">
                  <img
                    src={moreMedal}
                    alt="nova medalha"
                    className="medal__img"
                  />
                  <span className="medal__value">
                    <TfiMedall />
                  </span>
                </div>
              )}
            </CourseBannerContent>
          </CourseBanner>
        </CardContent>
      </Card>

      <Tabs defaultValue={props.selectedTab || 'sobre'}>
        <TabsList className="mb-6">
          <TabsTrigger
            value="sobre"
            onClick={() => navigate(`${BASE_ROUTE}/sobre`)}
          >
            Sobre
          </TabsTrigger>
          <TabsTrigger
            value="arquivos"
            onClick={() => navigate(`${BASE_ROUTE}/arquivos`)}
          >
            Arquivos
          </TabsTrigger>
          <TabsTrigger
            value="atividades"
            onClick={() => navigate(`${BASE_ROUTE}/atividades`)}
          >
            Atividades
          </TabsTrigger>
          {currentModule?.['learning-system-question-books']?.length > 0 && abilities?.['avaliacoes-apostilas'] && (
            <TabsTrigger
              value="apostilas"
              onClick={() => navigate(`${BASE_ROUTE}/apostilas`)}
            >
              Apostilas
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="sobre">
          <CourseModuleAboutTabAbout currentModule={currentModule} />
        </TabsContent>
        <TabsContent value="arquivos">
          <CourseModuleAboutTabFiles currentModule={currentModule} />
        </TabsContent>
        <TabsContent value="atividades">
          <CourseModuleAboutTabActivities currentModule={currentModule} />
        </TabsContent>
        <TabsContent value="apostilas">
          <CourseModuleAboutTabBooklet currentModule={currentModule} />
        </TabsContent>
      </Tabs>

      {/* <div className="show-course-sidebar">
        <ClassroomModuleList>
          {(learnSystemContents || []).map((learnSystemContent, index) => {
            const learningSystemItems = learnSystemContent['learning-system-items'] || [];

            if (learningSystemItems.some(learningSystemItem => learningSystemItem.unrequired)) {
              learnSystemContent['is-allowed'] = true;
            }

            return (
              <ClassModuleItem
                learnSystemContents={learnSystemContents}
                learnigSystem={learnigSystem}
                learningSystemItems={learningSystemItems}
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                selectedContent={selectedContent}
                setSelectedContent={setSelectedContent}
                learnSystemContent={learnSystemContent}
                blocked={blocked}
                setBlocked={setBlocked}
                active={active}
                setActive={setActive}
                authorityLevel={authorityLevel}
                index={index}
                key={index}
              />
            );
          })}
        </ClassroomModuleList>
      </div> */}
    </DashboardContent>
  );
}
